import { useState } from "react";
import { useIntl } from "react-intl";
import { useToast } from "@packsize/toast";

import type { EmServiceCommand } from "@/api/machines/em-api-v2";
import { useEmMachine } from "@/hooks/machines/em";
import { useEmServiceCommand } from "@/hooks/em/use-em-service-command";

import { EmManualControlCardErrorTemplate } from "./em-manual-control-card-error.template";
import { EmManualControlCardLoadingTemplate } from "./em-manual-control-card-loading.template";
import { EmManualControlCardTemplate } from "./em-manual-control-card.template";

export interface EmManualControlCardProps {
	readonly machineId: string;
}

export const EmManualControlCard = ({ machineId }: EmManualControlCardProps) => {
	const intl = useIntl();
	const { toastError, toastSuccess } = useToast();

	const [selectedTrackNumber, setSelectedTrackNumber] = useState(1);
	const [executingAction, setExecutingAction] = useState<EmServiceCommand | null>(null);

	const machineQuery = useEmMachine(machineId);
	const emServiceCommand = useEmServiceCommand();

	const handleAction = async (command: EmServiceCommand): Promise<void> => {
		setExecutingAction(command);

		try {
			await emServiceCommand.mutateAsync({
				machineId,
				trackNumber: selectedTrackNumber,
				command,
			});
			toastSuccess({
				message: intl.formatMessage({
					id: "Manual action sent to the machine",
					defaultMessage: "Manual action sent to the machine",
				}),
			});
		} catch {
			toastError({
				message: intl.formatMessage({
					id: "Failed to send manual action to the machine",
					defaultMessage: "Failed to send manual action to the machine",
				}),
			});
		} finally {
			setExecutingAction(null);
		}
	};

	if (machineQuery.isLoading) {
		return <EmManualControlCardLoadingTemplate />;
	}

	if (machineQuery.error || !machineQuery.data) {
		return <EmManualControlCardErrorTemplate onRetry={() => void machineQuery.refetch()} />;
	}

	return (
		<EmManualControlCardTemplate
			machine={machineQuery.data}
			selectedTrackNumber={selectedTrackNumber}
			executingAction={executingAction}
			onTrackChange={setSelectedTrackNumber}
			onAction={handleAction}
		/>
	);
};
